/**
 * Data Transformation Utilities for Analytics
 * Handles outlier detection, log scaling, regression analysis
 */

/**
 * Remove outliers using IQR (Interquartile Range) method
 * @param {Array} data - Array of data points
 * @param {String} field - Field name to analyze
 * @param {Number} multiplier - IQR multiplier (default 1.5, use 3.0 for extreme outliers only)
 * @returns {Object} { cleaned, outliers, stats }
 */
export const removeOutliers = (data, field, multiplier = 1.5) => {
  if (!data || data.length === 0) {
    return { cleaned: [], outliers: [], stats: null };
  }

  // Extract and sort values
  const values = data
    .map(d => d[field])
    .filter(v => v !== null && v !== undefined && !isNaN(v))
    .sort((a, b) => a - b);

  if (values.length === 0) {
    return { cleaned: data, outliers: [], stats: null };
  }

  // Calculate quartiles
  const q1Index = Math.floor(values.length * 0.25);
  const q3Index = Math.floor(values.length * 0.75);
  const medianIndex = Math.floor(values.length * 0.50);

  const q1 = values[q1Index];
  const q3 = values[q3Index];
  const median = values[medianIndex];
  const iqr = q3 - q1;

  // Calculate bounds
  const lowerBound = q1 - (multiplier * iqr);
  const upperBound = q3 + (multiplier * iqr);

  // Separate clean data from outliers
  const cleaned = data.filter(d => {
    const val = d[field];
    return val >= lowerBound && val <= upperBound;
  });

  const outliers = data.filter(d => {
    const val = d[field];
    return val < lowerBound || val > upperBound;
  });

  return {
    cleaned,
    outliers,
    stats: {
      originalCount: data.length,
      cleanedCount: cleaned.length,
      outlierCount: outliers.length,
      q1,
      q3,
      median,
      iqr,
      lowerBound,
      upperBound,
      multiplier
    }
  };
};

/**
 * Apply logarithmic transformation (base 10)
 * @param {Array} data - Array of data points
 * @param {String} xField - X-axis field name
 * @param {String} yField - Y-axis field name
 * @returns {Array} Data with log-transformed coordinates
 */
export const applyLogScale = (data, xField, yField) => {
  return data.map(d => ({
    ...d,
    x: Math.log10(Math.max(d[xField] || 1, 1)), // Ensure positive values
    y: Math.log10(Math.max(d[yField] || 1, 1)),
    x_original: d[xField],
    y_original: d[yField]
  }));
};

/**
 * Calculate linear regression
 * @param {Array} data - Array of data points
 * @param {String} xField - X-axis field name
 * @param {String} yField - Y-axis field name
 * @returns {Object} { slope, intercept, r2, trendLine }
 */
export const calculateLinearRegression = (data, xField, yField) => {
  const validData = data.filter(d =>
    d[xField] !== null &&
    d[xField] !== undefined &&
    !isNaN(d[xField]) &&
    d[yField] !== null &&
    d[yField] !== undefined &&
    !isNaN(d[yField])
  );

  if (validData.length < 2) {
    return null;
  }

  const n = validData.length;
  const sumX = validData.reduce((sum, d) => sum + d[xField], 0);
  const sumY = validData.reduce((sum, d) => sum + d[yField], 0);
  const sumXY = validData.reduce((sum, d) => sum + (d[xField] * d[yField]), 0);
  const sumX2 = validData.reduce((sum, d) => sum + (d[xField] ** 2), 0);
  const sumY2 = validData.reduce((sum, d) => sum + (d[yField] ** 2), 0);

  // Calculate slope and intercept
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
  const intercept = (sumY - slope * sumX) / n;

  // Calculate R² (coefficient of determination)
  const yMean = sumY / n;
  const ssTotal = validData.reduce((sum, d) => sum + ((d[yField] - yMean) ** 2), 0);
  const ssResidual = validData.reduce((sum, d) => {
    const predicted = slope * d[xField] + intercept;
    return sum + ((d[yField] - predicted) ** 2);
  }, 0);

  const r2 = ssTotal > 0 ? 1 - (ssResidual / ssTotal) : 0;

  // Generate trend line points
  const xValues = validData.map(d => d[xField]).sort((a, b) => a - b);
  const minX = xValues[0];
  const maxX = xValues[xValues.length - 1];

  const trendLine = [
    { x: minX, y: slope * minX + intercept },
    { x: maxX, y: slope * maxX + intercept }
  ];

  return {
    slope,
    intercept,
    r2: Math.max(0, Math.min(1, r2)), // Clamp between 0 and 1
    trendLine,
    dataPoints: n
  };
};

/**
 * Format R² value for display
 * @param {Number} r2 - R-squared value
 * @returns {String} Formatted string with interpretation
 */
export const formatR2 = (r2) => {
  if (r2 === null || r2 === undefined || isNaN(r2)) return 'N/A';

  const formatted = r2.toFixed(3);
  let strength = '';

  if (r2 >= 0.9) strength = 'very strong';
  else if (r2 >= 0.7) strength = 'strong';
  else if (r2 >= 0.5) strength = 'moderate';
  else if (r2 >= 0.3) strength = 'weak';
  else strength = 'very weak';

  return `R² = ${formatted} (${strength} correlation)`;
};

/**
 * Calculate statistics for a field
 * @param {Array} data - Array of data points
 * @param {String} field - Field name
 * @returns {Object} Statistics object
 */
export const calculateStats = (data, field) => {
  const values = data
    .map(d => d[field])
    .filter(v => v !== null && v !== undefined && !isNaN(v))
    .sort((a, b) => a - b);

  if (values.length === 0) {
    return null;
  }

  const sum = values.reduce((a, b) => a + b, 0);
  const mean = sum / values.length;

  // Calculate standard deviation
  const squaredDiffs = values.map(v => (v - mean) ** 2);
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  const stdDev = Math.sqrt(variance);

  return {
    count: values.length,
    min: values[0],
    max: values[values.length - 1],
    mean,
    median: values[Math.floor(values.length / 2)],
    stdDev,
    q1: values[Math.floor(values.length * 0.25)],
    q3: values[Math.floor(values.length * 0.75)]
  };
};

/**
 * Determine if log scale should be used
 * Criteria: Range spans more than 2 orders of magnitude
 * @param {Array} data - Array of data points
 * @param {String} field - Field name
 * @returns {Boolean} Whether log scale is recommended
 */
export const shouldUseLogScale = (data, field) => {
  const stats = calculateStats(data, field);
  if (!stats || stats.min <= 0) return false;

  const ratio = stats.max / stats.min;
  return ratio > 100; // More than 2 orders of magnitude
};

/**
 * Normalize bubble sizes for visualization
 * @param {Array} data - Array of data points
 * @param {String} sizeField - Field to use for bubble size
 * @param {Number} minSize - Minimum bubble radius
 * @param {Number} maxSize - Maximum bubble radius
 * @returns {Array} Data with normalized bubble sizes
 */
export const normalizeBubbleSizes = (data, sizeField, minSize = 20, maxSize = 200) => {
  const values = data.map(d => d[sizeField]).filter(v => v > 0);
  if (values.length === 0) return data;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  return data.map(d => {
    let size = minSize;
    if (range > 0 && d[sizeField] > 0) {
      const normalized = (d[sizeField] - min) / range;
      size = minSize + (normalized * (maxSize - minSize));
    }
    return {
      ...d,
      z: Math.round(size), // z = bubble radius
      [sizeField + '_original']: d[sizeField]
    };
  });
};

/**
 * Generate transformation info for display
 * @param {Object} options - Transformation options
 * @returns {Object} Info object for popup
 */
export const generateTransformationInfo = (options) => {
  const {
    chartId,
    chartTitle,
    outlierStats,
    useLogScale,
    regression,
    bubbleField,
    sampleSize
  } = options;

  const procedures = [];

  if (outlierStats) {
    procedures.push(
      `✓ Outlier removal: ${outlierStats.outlierCount} extreme values removed using IQR method (${outlierStats.multiplier}× multiplier)`
    );
  }

  if (useLogScale) {
    procedures.push('✓ Log₁₀ scale applied to both axes for better visibility');
  }

  if (bubbleField) {
    procedures.push(`✓ Bubble size represents ${bubbleField}`);
  }

  if (regression && regression.r2 !== null) {
    procedures.push(`✓ Linear regression: ${formatR2(regression.r2)}`);
  }

  // DYNAMIC INTERPRETATION - Smart heuristics based on actual data
  let interpretation = '';
  if (regression && regression.r2 !== null && regression.dataPoints) {
    const r2 = regression.r2;
    const n = regression.dataPoints;
    const direction = regression.slope > 0 ? 'positive' : 'negative';

    // Confidence adjustment based on sample size
    const sampleConfidence = n < 20 ? 'limited' : n < 50 ? 'moderate' : 'strong';

    // Dynamic correlation strength (considering sample size)
    let strength = 'no';
    let strengthDesc = 'no clear pattern';

    if (r2 > 0.7) {
      strength = 'strong';
      strengthDesc = n < 20 ? 'strong pattern (verify with more data)' : 'strong, reliable pattern';
    } else if (r2 > 0.5) {
      strength = 'moderate-to-strong';
      strengthDesc = 'meaningful relationship';
    } else if (r2 > 0.3) {
      strength = 'moderate';
      strengthDesc = 'noticeable trend';
    } else if (r2 > 0.1) {
      strength = 'weak';
      strengthDesc = 'slight tendency';
    } else {
      strength = 'minimal';
      strengthDesc = 'scattered relationship';
    }

    // Build dynamic interpretation
    interpretation = `${strengthDesc.charAt(0).toUpperCase() + strengthDesc.slice(1)} showing ${direction} correlation (R²=${r2.toFixed(3)}, n=${n}).`;

    // Add practical insight based on strength and sample size
    if (r2 > 0.5 && n >= 30) {
      interpretation += ' This relationship is statistically significant and actionable.';
    } else if (r2 > 0.5 && n < 30) {
      interpretation += ' Interesting trend - consider collecting more data for validation.';
    } else if (r2 < 0.3) {
      interpretation += ' Other factors likely have greater influence.';
    }
  }

  return {
    chartId,
    chartTitle,
    procedures,
    interpretation,
    sampleSize: sampleSize || 'N/A',
    technicalDetails: {
      outlierStats,
      regression,
      useLogScale
    }
  };
};
